import React, { useState, useEffect } from 'react';
import { adminsAPI } from '../../services/api';
import { formatClockDate, formatDmy } from '../../utils/formatters';
import { 
  FiUserPlus, FiEdit2, FiTrash2, FiLock, FiX, FiCheck,
  FiAlertCircle, FiCheckCircle 
} from 'react-icons/fi';

export default function AdminsManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({ username: '', full_name: '', password: '' });
  const [pwForm, setPwForm] = useState({ 
    current_password: '', 
    new_password: '', 
    confirm_password: '' 
  });

  useEffect(() => { loadAdmins(); }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const res = await adminsAPI.list();
      setAdmins(res.data.admins || []);
    } catch (err) {
      setError('Adminlarni yuklashda xato');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const handleAdd = async () => {
    try {
      await adminsAPI.create(formData);
      showMessage('Admin qo\'shildi');
      setShowAddModal(false);
      setFormData({ username: '', full_name: '', password: '' });
      loadAdmins();
    } catch (err) {
      showMessage(err.response?.data?.error || 'Xato', true);
    }
  };

  const handleEdit = async () => {
    try {
      const data = { username: formData.username, full_name: formData.full_name };
      if (formData.password) data.password = formData.password;
      
      await adminsAPI.update(editingAdmin.id, data);
      showMessage('Admin yangilandi');
      setShowEditModal(false);
      setEditingAdmin(null);
      setFormData({ username: '', full_name: '', password: '' });
      loadAdmins();
    } catch (err) {
      showMessage(err.response?.data?.error || 'Xato', true);
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`${admin.full_name} ni o'chirishni tasdiqlaysizmi?`)) return;
    try {
      await adminsAPI.delete(admin.id);
      showMessage('Admin o\'chirildi');
      loadAdmins();
    } catch (err) {
      showMessage(err.response?.data?.error || 'Xato', true);
    }
  };

  const handlePasswordChange = async () => {
    try {
      if (pwForm.new_password !== pwForm.confirm_password) {
        showMessage('Yangi parollar mos kelmaydi', true);
        return;
      }
      const res = await adminsAPI.changePassword({
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      
      if (res.data.requires_relogin) {
        showMessage('Parol o\'zgartirildi. 2 sekund ichida qayta kirish sahifasiga o\'tasiz...');
        setTimeout(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }, 2000);
      } else {
        showMessage('Parol o\'zgartirildi');
        setShowPasswordModal(false);
        setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      }
    } catch (err) {
      showMessage(err.response?.data?.error || 'Xato', true);
    }
  };

  const styles = {
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    btnGroup: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
    btn: {
      padding: '10px 16px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontWeight: '500',
    },
    btnPrimary: { background: '#3b82f6', color: 'white' },
    btnSecondary: { background: '#374151', color: 'white' },
    btnIcon: {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: '#9ca3af',
      padding: '6px',
      borderRadius: '4px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      background: '#1f2937',
      borderRadius: '8px',
      overflow: 'hidden',
    },
    th: {
      padding: '12px 16px',
      textAlign: 'left',
      background: '#111827',
      fontSize: '12px',
      color: '#9ca3af',
      textTransform: 'uppercase',
      fontWeight: '600',
    },
    td: {
      padding: '14px 16px',
      borderTop: '1px solid #374151',
      fontSize: '14px',
      color: '#e5e7eb',
    },
    modal: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      background: '#1f2937',
      borderRadius: '12px',
      padding: '24px',
      width: '450px',
      maxWidth: '90%',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '13px',
      color: '#9ca3af',
      fontWeight: '500',
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '6px',
      border: '1px solid #374151',
      background: '#111827',
      color: 'white',
      fontSize: '14px',
      marginBottom: '14px',
      boxSizing: 'border-box',
    },
    alert: {
      padding: '12px',
      borderRadius: '6px',
      marginBottom: '16px',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    alertError: { background: '#7f1d1d', color: '#fecaca' },
    alertSuccess: { background: '#14532d', color: '#bbf7d0' },
    helpText: {
      fontSize: '12px',
      color: '#6b7280',
      marginTop: '-10px',
      marginBottom: '14px',
    },
  };

  const passwordRequirements = "Kamida 8 belgi, katta-kichik harf va raqam";

  if (loading) {
    return <div style={{ padding: '20px', color: '#9ca3af' }}>Yuklanmoqda...</div>;
  }

  return (
    <div>
      <div style={styles.headerRow}>
        <h3 style={{ color: 'white', margin: 0 }}>Adminlar boshqaruvi</h3>
        <div style={styles.btnGroup}>
          <button 
            style={{...styles.btn, ...styles.btnSecondary}}
            onClick={() => setShowPasswordModal(true)}
          >
            <FiLock /> Parolni o'zgartirish
          </button>
          <button 
            style={{...styles.btn, ...styles.btnPrimary}}
            onClick={() => {
              setFormData({ username: '', full_name: '', password: '' });
              setShowAddModal(true);
            }}
          >
            <FiUserPlus /> Yangi admin
          </button>
        </div>
      </div>

      {error && (
        <div style={{...styles.alert, ...styles.alertError}}>
          <FiAlertCircle /> {error}
        </div>
      )}
      {success && (
        <div style={{...styles.alert, ...styles.alertSuccess}}>
          <FiCheckCircle /> {success}
        </div>
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Username</th>
            <th style={styles.th}>To'liq ism</th>
            <th style={styles.th}>Oxirgi kirish</th>
            <th style={styles.th}>Yaratilgan</th>
            <th style={{...styles.th, textAlign: 'right'}}>Amallar</th>
          </tr>
        </thead>
        <tbody>
          {admins.map(a => (
            <tr key={a.id}>
              <td style={styles.td}>{a.username}</td>
              <td style={styles.td}>{a.full_name}</td>
              <td style={styles.td}>
                {a.last_login_at ? formatClockDate(a.last_login_at) : '—'}
              </td>
              <td style={styles.td}>
                {formatDmy(a.created_at)}
              </td>
              <td style={{...styles.td, textAlign: 'right'}}>
                <button 
                  style={styles.btnIcon}
                  onClick={() => {
                    setEditingAdmin(a);
                    setFormData({ username: a.username, full_name: a.full_name, password: '' });
                    setShowEditModal(true);
                  }}
                  title="Tahrirlash"
                >
                  <FiEdit2 size={16} />
                </button>
                <button 
                  style={{...styles.btnIcon, color: '#ef4444'}}
                  onClick={() => handleDelete(a)}
                  title="O'chirish"
                >
                  <FiTrash2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add modal */}
      {showAddModal && (
        <div style={styles.modal} onClick={() => setShowAddModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ color: 'white', margin: 0 }}>Yangi admin qo'shish</h3>
              <button style={styles.btnIcon} onClick={() => setShowAddModal(false)}>
                <FiX size={20} />
              </button>
            </div>
            <label style={styles.label}>Username</label>
            <input 
              style={styles.input}
              value={formData.username}
              onChange={e => setFormData({...formData, username: e.target.value})}
              placeholder="3-30 belgi, faqat harf, raqam, _"
            />
            <label style={styles.label}>To'liq ism</label>
            <input 
              style={styles.input}
              value={formData.full_name}
              onChange={e => setFormData({...formData, full_name: e.target.value})}
              placeholder="Ism Familiya"
            />
            <label style={styles.label}>Parol</label>
            <input 
              type="password"
              style={styles.input}
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              placeholder={passwordRequirements}
            />
            <p style={styles.helpText}>{passwordRequirements}</p>
            <button 
              style={{...styles.btn, ...styles.btnPrimary, width: '100%', justifyContent: 'center'}}
              onClick={handleAdd}
            >
              <FiCheck /> Qo'shish
            </button>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {showEditModal && editingAdmin && (
        <div style={styles.modal} onClick={() => setShowEditModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ color: 'white', margin: 0 }}>Adminni tahrirlash</h3>
              <button style={styles.btnIcon} onClick={() => setShowEditModal(false)}>
                <FiX size={20} />
              </button>
            </div>
            <label style={styles.label}>Username</label>
            <input 
              style={styles.input}
              value={formData.username}
              onChange={e => setFormData({...formData, username: e.target.value})}
            />
            <label style={styles.label}>To'liq ism</label>
            <input 
              style={styles.input}
              value={formData.full_name}
              onChange={e => setFormData({...formData, full_name: e.target.value})}
            />
            <label style={styles.label}>Yangi parol (ixtiyoriy)</label>
            <input 
              type="password"
              style={styles.input}
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              placeholder="Bo'sh - parol o'zgarmaydi"
            />
            {formData.password && <p style={styles.helpText}>{passwordRequirements}</p>}
            <button 
              style={{...styles.btn, ...styles.btnPrimary, width: '100%', justifyContent: 'center'}}
              onClick={handleEdit}
            >
              <FiCheck /> Saqlash
            </button>
          </div>
        </div>
      )}

      {/* Password change modal */}
      {showPasswordModal && (
        <div style={styles.modal} onClick={() => setShowPasswordModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ color: 'white', margin: 0 }}>Parolni o'zgartirish</h3>
              <button style={styles.btnIcon} onClick={() => setShowPasswordModal(false)}>
                <FiX size={20} />
              </button>
            </div>
            <label style={styles.label}>Joriy parol</label>
            <input 
              type="password"
              style={styles.input}
              value={pwForm.current_password}
              onChange={e => setPwForm({...pwForm, current_password: e.target.value})}
              autoComplete="current-password"
            />
            <label style={styles.label}>Yangi parol</label>
            <input 
              type="password"
              style={styles.input}
              value={pwForm.new_password}
              onChange={e => setPwForm({...pwForm, new_password: e.target.value})}
              placeholder={passwordRequirements}
              autoComplete="new-password"
            />
            <p style={styles.helpText}>{passwordRequirements}</p>
            <label style={styles.label}>Yangi parolni tasdiqlang</label>
            <input 
              type="password"
              style={styles.input}
              value={pwForm.confirm_password}
              onChange={e => setPwForm({...pwForm, confirm_password: e.target.value})}
              autoComplete="new-password"
            />
            <button 
              style={{...styles.btn, ...styles.btnPrimary, width: '100%', justifyContent: 'center'}}
              onClick={handlePasswordChange}
            >
              <FiCheck /> O'zgartirish
            </button>
            <p style={{...styles.helpText, marginTop: '12px', textAlign: 'center'}}>
              Parol o'zgartirilgandan keyin qayta kirish kerak bo'ladi
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
